'use client';

import React from 'react';
import { animate, inView } from '@motionone/dom';
import {
  clearMotionInline,
  MOTION_DURATION,
  MOTION_EASING,
  prefersReducedMotion,
  setMotionHidden,
} from '@/lib/motion-one';

type MotionRevealProps = {
  children: React.ReactNode;
  /** Animate immediately on mount (for above-the-fold content). */
  immediate?: boolean;
  delay?: number;
  y?: number;
  as?: 'div' | 'section';
} & React.HTMLAttributes<HTMLElement>;

export function MotionReveal({
  children,
  className,
  immediate = false,
  delay = 0,
  y = 16,
  as: Tag = 'div',
  ...rest
}: MotionRevealProps) {
  const ref = React.useRef<HTMLElement>(null);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (prefersReducedMotion()) {
      clearMotionInline(el);
      return;
    }

    setMotionHidden(el, y);

    const run = () => {
      animate(
        el,
        { opacity: 1, transform: 'translateY(0)' },
        { duration: MOTION_DURATION.normal, delay, easing: MOTION_EASING },
      );
    };

    if (immediate) {
      run();
      return;
    }

    const stop = inView(el, run, { amount: 'any' });
    return stop;
  }, [delay, immediate, y]);

  return React.createElement(Tag, { ref, className, ...rest }, children);
}
